import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Loader2, Mic, MonitorSpeaker, Upload } from "lucide-react";
import { useRecorder, type AudioSource } from "@/recorder/context";
import { AUDIO_ACCEPT } from "@/recorder/audioFile";

const SOURCE_OPTIONS: {
  value: AudioSource;
  label: string;
  hint: string;
  icon: typeof Mic;
}[] = [
  { value: "mic", label: "Default mic", hint: "System default input", icon: Mic },
  {
    value: "system",
    label: "Device audio",
    hint: "Share a tab or screen with audio",
    icon: MonitorSpeaker,
  },
];

const MENU_WIDTH = 240;
/** Rough height, only used to decide whether the menu fits below the button. */
const MENU_HEIGHT = 230;
const GAP = 6;

type Placement = { left: number; top?: number; bottom?: number; up: boolean };

const SIZES = {
  sm: {
    shell: "h-7",
    main: "min-w-[88px] pl-3.5 pr-3",
    chevron: "w-8",
  },
  md: {
    shell: "h-9",
    main: "min-w-[104px] pl-4 pr-3.5",
    chevron: "w-9",
  },
} as const;

/**
 * Record, with the audio source and "upload a file instead" one click away.
 *
 * The menu is portalled to <body> with fixed positioning: inside the lesson
 * dialog the button sits in a scrolling container that would clip an absolutely
 * positioned menu, and near the bottom of the screen it flips upwards.
 */
export default function RecordSplitButton({
  size,
  align = "end",
  disabled = false,
  starting = false,
  onRecord,
  onFiles,
}: {
  size: keyof typeof SIZES;
  /** Which edge of the button the menu lines up with. */
  align?: "start" | "end";
  disabled?: boolean;
  starting?: boolean;
  onRecord: () => void;
  onFiles: (files: File[]) => void;
}) {
  const recorder = useRecorder();
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement>({ left: 0, top: 0, up: false });
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const classes = SIZES[size];
  const SourceIcon = recorder.source === "system" ? MonitorSpeaker : Mic;

  const place = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect === undefined) return;
    const left = Math.min(
      Math.max(8, align === "start" ? rect.left : rect.right - MENU_WIDTH),
      window.innerWidth - MENU_WIDTH - 8,
    );
    const below = window.innerHeight - rect.bottom;
    const up = below < MENU_HEIGHT + GAP + 8 && rect.top > below;
    setPlacement(
      up
        ? { left, bottom: window.innerHeight - rect.top + GAP, up }
        : { left, top: rect.bottom + GAP, up },
    );
  }, [align]);

  // Pointer events cover mouse and touch alike, so a tap outside closes the
  // menu on phones too. Scrolling or resizing would leave a fixed menu
  // floating away from its button, so both close it as well.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onScroll = (event: Event) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const isDisabled = disabled || starting;

  return (
    <>
      <div
        ref={triggerRef}
        className={`box-border flex ${classes.shell} items-stretch overflow-hidden rounded-xl bg-[#222] text-[#fdfdfc] shadow-xs transition-shadow hover:shadow-sm dark:bg-[#ededeb] dark:text-[#141413]`}
      >
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => {
            setOpen(false);
            onRecord();
          }}
          title={recorder.source === "system" ? "Record device audio" : "Record microphone"}
          className={`flex ${classes.main} cursor-pointer items-center justify-center gap-1.5 text-xs font-semibold tracking-wide transition-colors hover:bg-[#111] active:bg-black disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-white dark:active:bg-white`}
        >
          {starting ? (
            <>
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
              <span>Starting…</span>
            </>
          ) : (
            <>
              <SourceIcon className="h-3.5 w-3.5 shrink-0" />
              <span>Record</span>
            </>
          )}
        </button>
        <div className="my-1.5 w-px bg-white/20 dark:bg-black/15" />
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => {
            // Measured on open rather than in an effect, so the first frame of
            // the menu is already in the right place.
            if (!open) place();
            setOpen(!open);
          }}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Audio source and file upload"
          title="Audio source or upload a file"
          className={`flex ${classes.chevron} cursor-pointer items-center justify-center transition-colors hover:bg-[#111] active:bg-black disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-white dark:active:bg-white`}
        >
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={AUDIO_ACCEPT}
        multiple
        className="hidden"
        tabIndex={-1}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          // Cleared so picking the same file twice still fires a change.
          event.target.value = "";
          if (files.length > 0) onFiles(files);
        }}
      />

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={menuRef}
              role="menu"
              initial={{ opacity: 0, y: placement.up ? 4 : -4, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: placement.up ? 4 : -4, scale: 0.97 }}
              transition={{ duration: 0.12 }}
              style={{
                position: "fixed",
                left: placement.left,
                top: placement.top,
                bottom: placement.bottom,
                width: MENU_WIDTH,
              }}
              className={`z-[60] rounded-xl border border-[#d1d1cf] bg-[#fdfdfc] p-1 shadow-lg dark:border-[#2a2a28] dark:bg-[#141413] ${placement.up ? "origin-bottom" : "origin-top"}`}
            >
              <div className="px-2.5 pt-1.5 pb-1 font-mono text-[10px] tracking-wider text-neutral-400 uppercase">
                Audio source
              </div>
              {SOURCE_OPTIONS.map((option) => {
                const Icon = option.icon;
                const unavailable = option.value === "system" && !recorder.systemAudioSupported;
                const selected = recorder.source === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    disabled={unavailable}
                    onClick={() => {
                      recorder.setSource(option.value);
                      setOpen(false);
                    }}
                    className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left transition-colors hover:bg-neutral-900/[0.05] disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent sm:py-2 dark:hover:bg-white/[0.08]"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-neutral-500 dark:text-neutral-400" />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="text-xs font-medium text-[#222] dark:text-[#ededeb]">
                        {option.label}
                      </span>
                      <span className="text-[11px] text-neutral-500">
                        {unavailable ? "Not supported on this device" : option.hint}
                      </span>
                    </span>
                    {selected && (
                      <Check className="h-3.5 w-3.5 shrink-0 stroke-[2.5] text-[#222] dark:text-[#ededeb]" />
                    )}
                  </button>
                );
              })}

              <div className="mx-2 my-1 h-px bg-[#d1d1cf]/70 dark:bg-[#2a2a28]" />

              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  fileRef.current?.click();
                }}
                className="group flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left transition-colors hover:bg-neutral-900/[0.05] sm:py-2 dark:hover:bg-white/[0.08]"
              >
                <Upload className="h-4 w-4 shrink-0 text-neutral-500 transition-transform duration-200 group-hover:-translate-y-0.5 dark:text-neutral-400" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-xs font-medium text-[#222] dark:text-[#ededeb]">
                    Upload audio files…
                  </span>
                  <span className="text-[11px] text-neutral-500">
                    Or drop one anywhere in the app
                  </span>
                </span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}
